import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, Plus, Trash2, X, ArrowLeft, ChevronLeft, ChevronRight,
  Truck, MessageSquare, Send, Image, AlertCircle, Check, Loader2,
  Pencil, Save, Download,
} from 'lucide-react';

const INCIDENTS_PER_PAGE = 10;
const COMMENTS_PER_PAGE = 5;

const TIPO_INCIDENCIA_OPTIONS = ['Bulto Dañado', 'Pérdida', 'fuera de Plazo'];

const TIPO_BADGE = {
  'Bulto Dañado': 'bg-yellow-100 text-yellow-800',
  'Pérdida': 'bg-red-100 text-red-800',
  'fuera de Plazo': 'bg-orange-100 text-orange-800',
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ========== PAGINATION ========== */
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  const getVisiblePages = () => {
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  const visiblePages = getVisiblePages();
  return (
    <div className="flex items-center justify-center gap-2 py-3">
      <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {visiblePages.map((p) => (
        <button key={p} onClick={() => onPageChange(p)} className={`px-3 py-1.5 text-sm rounded-lg transition-colors font-medium ${p === page ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          {p}
        </button>
      ))}
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ========== MAIN COMPONENT ========== */
const AgenciasTransporte = () => {
  const { token, API_URL } = useAuth();

  // ── View state ──
  const [view, setView] = useState('list'); // 'list' | 'incidents'
  const [selectedAgency, setSelectedAgency] = useState(null);

  // ── Agencies state ──
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Agency modal ──
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ nombre: '', responsable_comercial: '', correo_comercial: '', numero_telefono: '' });
  const [savingAgency, setSavingAgency] = useState(false);

  // ── Inline editing ──
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editingValue, setEditingValue] = useState('');

  // ── Delete confirm ──
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── Incidents state ──
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [incidentsPage, setIncidentsPage] = useState(1);
  const [incidentsTotalPages, setIncidentsTotalPages] = useState(1);
  const [selectedIncident, setSelectedIncident] = useState(null);

  // ── Incident modal ──
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentForm, setIncidentForm] = useState({ tipo_incidencia: 'Bulto Dañado', numero_expedicion: '', fecha: '', numero_factura: '', comentarios: '' });
  const [incidentPhotos, setIncidentPhotos] = useState([]);
  const [savingIncident, setSavingIncident] = useState(false);

  // ── Batch updates ──
  const [pendingChanges, setPendingChanges] = useState({}); // { [incidentId]: { resuelto?, cobro_porte? } }
  const [savingBatch, setSavingBatch] = useState(false);

  // ── Comments state ──
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsTotalPages, setCommentsTotalPages] = useState(1);
  const [newComment, setNewComment] = useState('');
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [savingComment, setSavingComment] = useState(false);

  // ── Download state ──
  const [downloadDesde, setDownloadDesde] = useState('');
  const [downloadHasta, setDownloadHasta] = useState('');
  const [downloading, setDownloading] = useState(false);

  const commentFileRef = useRef(null);
  const incidentFileRef = useRef(null);

  // ── Clear alerts after 4s ──
  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => { setError(''); setSuccess(''); }, 4000);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  // ════════════════════════════════════════════
  // ── AGENCIES CRUD ──
  // ════════════════════════════════════════════

  const fetchAgencies = useCallback(async (search = '') => {
    setLoadingAgencies(true);
    try {
      const params = search.trim() ? `?search=${encodeURIComponent(search)}` : '';
      const res = await fetch(`${API_URL}/transportes/agencias${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar agencias');
      const data = await res.json();
      setAgencies(data.agencias || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAgencies(false);
    }
  }, [API_URL, token]);

  useEffect(() => { fetchAgencies(searchTerm); }, []);

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => fetchAgencies(searchTerm), 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const createAgency = async () => {
    if (!agencyForm.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSavingAgency(true);
    try {
      const res = await fetch(`${API_URL}/transportes/agencias`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(agencyForm),
      });
      if (!res.ok) throw new Error('Error al crear agencia');
      setShowAgencyModal(false);
      setAgencyForm({ nombre: '', responsable_comercial: '', correo_comercial: '', numero_telefono: '' });
      setSuccess('Agencia creada correctamente');
      fetchAgencies(searchTerm);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAgency(false);
    }
  };

  const saveInlineEdit = async (agencyId, field, value) => {
    try {
      const res = await fetch(`${API_URL}/transportes/agencias/${agencyId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error('Error al actualizar');
      setAgencies(prev => prev.map(a => a.id === agencyId ? { ...a, [field]: value } : a));
      setSuccess('Agencia actualizada');
    } catch (err) {
      setError(err.message);
    }
    setEditingCell(null);
  };

  const deleteAgency = async (id) => {
    try {
      const res = await fetch(`${API_URL}/transportes/agencias/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al eliminar agencia');
      setSuccess('Agencia eliminada');
      setDeleteConfirm(null);
      fetchAgencies(searchTerm);
    } catch (err) {
      setError(err.message);
    }
  };

  // ════════════════════════════════════════════
  // ── INCIDENTS CRUD ──
  // ════════════════════════════════════════════

  const fetchIncidents = useCallback(async (agencyId, page = 1) => {
    setLoadingIncidents(true);
    try {
      const res = await fetch(`${API_URL}/transportes/agencias/${agencyId}/incidencias?page=${page}&limit=${INCIDENTS_PER_PAGE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar incidencias');
      const data = await res.json();
      setIncidents(data.incidencias || data.data || []);
      setIncidentsTotalPages(data.totalPages || data.total_pages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingIncidents(false);
    }
  }, [API_URL, token]);

  const openIncidentsView = (agency) => {
    setSelectedAgency(agency);
    setView('incidents');
    setIncidentsPage(1);
    setSelectedIncident(null);
    setPendingChanges({});
    fetchIncidents(agency.id, 1);
  };

  useEffect(() => {
    if (view === 'incidents' && selectedAgency) {
      fetchIncidents(selectedAgency.id, incidentsPage);
    }
  }, [incidentsPage]);

  const createIncident = async () => {
    if (!incidentForm.tipo_incidencia || !incidentForm.numero_expedicion.trim()) {
      setError('Tipo de incidencia y Nº expedición son obligatorios');
      return;
    }
    setSavingIncident(true);
    try {
      const fd = new FormData();
      fd.append('tipo_incidencia', incidentForm.tipo_incidencia);
      fd.append('numero_expedicion', incidentForm.numero_expedicion);
      fd.append('fecha', incidentForm.fecha);
      fd.append('agencia_id', selectedAgency.id);
      fd.append('numero_factura', incidentForm.numero_factura);
      fd.append('comentarios', incidentForm.comentarios);
      incidentPhotos.forEach(f => fd.append('fotos', f));

      const res = await fetch(`${API_URL}/transportes/incidencias`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Error al crear incidencia');
      setShowIncidentModal(false);
      setIncidentForm({ tipo_incidencia: 'Bulto Dañado', numero_expedicion: '', fecha: '', numero_factura: '', comentarios: '' });
      setIncidentPhotos([]);
      setSuccess('Incidencia creada correctamente');
      fetchIncidents(selectedAgency.id, incidentsPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingIncident(false);
    }
  };

  const deleteIncident = async (id) => {
    try {
      const res = await fetch(`${API_URL}/transportes/incidencias/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al eliminar incidencia');
      setSuccess('Incidencia eliminada');
      if (selectedIncident?.id === id) setSelectedIncident(null);
      fetchIncidents(selectedAgency.id, incidentsPage);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleCheckbox = (incidentId, field) => {
    const incident = incidents.find(i => i.id === incidentId);
    const currentPending = pendingChanges[incidentId] || {};
    const currentValue = currentPending[field] !== undefined ? currentPending[field] : (incident[field] ? 1 : 0);
    const newValue = currentValue ? 0 : 1;

    setPendingChanges(prev => ({
      ...prev,
      [incidentId]: { ...prev[incidentId], [field]: newValue },
    }));
  };

  const getCheckboxValue = (incident, field) => {
    const pending = pendingChanges[incident.id];
    if (pending && pending[field] !== undefined) return !!pending[field];
    return !!incident[field];
  };

  const saveBatchChanges = async () => {
    const updates = Object.entries(pendingChanges).map(([id, fields]) => ({
      id: Number(id),
      ...fields,
    }));
    if (updates.length === 0) return;

    setSavingBatch(true);
    try {
      const res = await fetch(`${API_URL}/transportes/incidencias/batch-update`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error('Error al guardar cambios');
      setPendingChanges({});
      setSuccess('Cambios guardados correctamente');
      fetchIncidents(selectedAgency.id, incidentsPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingBatch(false);
    }
  };

  // ════════════════════════════════════════════
  // ── COMMENTS CRUD ──
  // ════════════════════════════════════════════

  const fetchComments = useCallback(async (incidentId, page = 1) => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${API_URL}/transportes/incidencias/${incidentId}/comentarios?page=${page}&limit=${COMMENTS_PER_PAGE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar comentarios');
      const data = await res.json();
      setComments(data.comentarios || data.data || []);
      setCommentsTotalPages(data.totalPages || data.total_pages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingComments(false);
    }
  }, [API_URL, token]);

  const selectIncident = (incident) => {
    setSelectedIncident(incident);
    setCommentsPage(1);
    setNewComment('');
    setCommentPhotos([]);
    fetchComments(incident.id, 1);
  };

  useEffect(() => {
    if (selectedIncident) {
      fetchComments(selectedIncident.id, commentsPage);
    }
  }, [commentsPage]);

  const addComment = async () => {
    if (!newComment.trim() && commentPhotos.length === 0) return;
    setSavingComment(true);
    try {
      const fd = new FormData();
      fd.append('comentario', newComment);
      commentPhotos.forEach(f => fd.append('fotos', f));

      const res = await fetch(`${API_URL}/transportes/incidencias/${selectedIncident.id}/comentarios`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Error al añadir comentario');
      setNewComment('');
      setCommentPhotos([]);
      setSuccess('Comentario añadido');
      fetchComments(selectedIncident.id, 1);
      setCommentsPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingComment(false);
    }
  };

  // ════════════════════════════════════════════
  // ── INLINE EDITABLE CELL ──
  // ════════════════════════════════════════════

  const InlineCell = ({ agency, field, value }) => {
    const isEditing = editingCell?.id === agency.id && editingCell?.field === field;

    if (isEditing) {
      return (
        <input
          autoFocus
          className="w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={editingValue}
          onChange={e => setEditingValue(e.target.value)}
          onBlur={() => saveInlineEdit(agency.id, field, editingValue)}
          onKeyDown={e => {
            if (e.key === 'Enter') saveInlineEdit(agency.id, field, editingValue);
            if (e.key === 'Escape') setEditingCell(null);
          }}
        />
      );
    }

    return (
      <span
        className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors inline-block min-w-[40px]"
        onClick={() => { setEditingCell({ id: agency.id, field }); setEditingValue(value || ''); }}
        title="Clic para editar"
      >
        {value || <span className="text-gray-300 italic">—</span>}
      </span>
    );
  };

  // ── Download incidents as CSV ──
  const downloadIncidents = async () => {
    if (!selectedAgency) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (downloadDesde) params.set('fecha_desde', downloadDesde);
      if (downloadHasta) params.set('fecha_hasta', downloadHasta);

      const res = await fetch(`${API_URL}/transportes/agencias/${selectedAgency.id}/incidencias/download?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Error al descargar');

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `incidencias_${selectedAgency.nombre}.csv`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Incidencias descargadas correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setDownloading(false);
    }
  };

  // ════════════════════════════════════════════
  // ── PHOTO THUMBNAILS ──
  // ════════════════════════════════════════════

  const INCIDENCIAS_PHOTOS_BASE = 'https://comprassanitarias.com/pages/agencias_transporte';

  const PhotoThumbnails = ({ photos, inline }) => {
    let list = [];
    try {
      list = !photos ? [] : typeof photos === 'string' ? JSON.parse(photos) : Array.isArray(photos) ? photos : [];
    } catch { list = []; }

    if (list.length === 0) {
      return inline ? <span className="text-xs text-gray-400">Sin fotos</span> : null;
    }

    return (
      <div className="flex gap-1.5 flex-wrap">
        {list.map((photo, idx) => {
          const url = typeof photo === 'string' ? photo : photo.url || photo.path;
          // Full URLs (new format) → use as-is
          // Legacy paths like "images/abc.jpg" → prepend base URL
          const fullUrl = url?.startsWith('http') ? url : `${INCIDENCIAS_PHOTOS_BASE}/${url}`;
          return (
            <a key={idx} href={fullUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img src={fullUrl} alt={`Foto ${idx + 1}`} className="w-20 h-20 object-cover rounded border border-gray-200 hover:border-blue-400 transition-colors" />
            </a>
          );
        })}
      </div>
    );
  };

  // ════════════════════════════════════════════
  // ── RENDER: AGENCY LIST VIEW ──
  // ════════════════════════════════════════════

  const renderAgencyList = () => (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-800">Agencias de Transporte</h1>
          </div>
          <button onClick={() => setShowAgencyModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> Nueva Agencia
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar agencias..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="mx-6 mt-3 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loadingAgencies ? (
          <div className="flex items-center justify-center h-40 gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Cargando agencias...</span>
          </div>
        ) : agencies.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No se encontraron agencias</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsable</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Incidencias</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {agencies.map(agency => (
                  <tr key={agency.id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <InlineCell agency={agency} field="nombre" value={agency.nombre} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <InlineCell agency={agency} field="responsable_comercial" value={agency.responsable_comercial} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <InlineCell agency={agency} field="correo_comercial" value={agency.correo_comercial} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <InlineCell agency={agency} field="numero_telefono" value={agency.numero_telefono} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {agency.total_incidencias ?? agency.incidencias_count ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openIncidentsView(agency)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          Ver Incidencias
                        </button>
                        {deleteConfirm === agency.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => deleteAgency(agency.id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors">Sí</button>
                            <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(agency.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ════════════════════════════════════════════
  // ── RENDER: INCIDENTS VIEW ──
  // ════════════════════════════════════════════

  const renderIncidentsView = () => (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); setSelectedAgency(null); setSelectedIncident(null); setPendingChanges({}); }} className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-800">{selectedAgency?.nombre}</h1>
              <p className="text-sm text-gray-500">Incidencias de la agencia</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {Object.keys(pendingChanges).length > 0 && (
              <button
                onClick={saveBatchChanges}
                disabled={savingBatch}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {savingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Cambios ({Object.keys(pendingChanges).length})
              </button>
            )}
            <button onClick={() => setShowIncidentModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
              <Plus className="w-4 h-4" /> Nueva Incidencia
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="mx-6 mt-3 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Split layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel: incidents table (60%) */}
        <div className="w-3/5 flex flex-col border-r border-gray-200 overflow-hidden">
          <div className="flex-1 overflow-auto px-4 py-4">
            {loadingIncidents ? (
              <div className="flex items-center justify-center h-40 gap-2 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Cargando incidencias...</span>
              </div>
            ) : incidents.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No hay incidencias registradas</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">N Expedición</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">N Factura</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fotos</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Resuelto</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cobro Porte</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {incidents.map(inc => (
                      <tr
                        key={inc.id}
                        onClick={() => selectIncident(inc)}
                        className={`cursor-pointer transition-colors ${selectedIncident?.id === inc.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-3 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_BADGE[inc.tipo_incidencia] || 'bg-gray-100 text-gray-700'}`}>
                            {inc.tipo_incidencia}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900">{inc.numero_expedicion}</td>
                        <td className="px-3 py-3 text-sm text-gray-600">{formatDate(inc.fecha)}</td>
                        <td className="px-3 py-3 text-sm text-gray-900">{inc.numero_factura || '—'}</td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <PhotoThumbnails photos={inc.fotos} inline />
                        </td>
                        <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={getCheckboxValue(inc, 'resuelto')}
                            onChange={() => toggleCheckbox(inc.id, 'resuelto')}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={getCheckboxValue(inc, 'cobro_porte')}
                            onChange={() => toggleCheckbox(inc.id, 'cobro_porte')}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <button onClick={() => deleteIncident(inc.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 border-t border-gray-200 px-4">
            <Pagination page={incidentsPage} totalPages={incidentsTotalPages} onPageChange={setIncidentsPage} />
          </div>

          {/* Download section */}
          <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Descargar Incidencias</p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Desde</label>
                <input type="date" value={downloadDesde} onChange={e => setDownloadDesde(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                <input type="date" value={downloadHasta} onChange={e => setDownloadHasta(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <button
                onClick={downloadIncidents}
                disabled={downloading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* Right panel: comments (40%) */}
        <div className="w-2/5 flex flex-col overflow-hidden bg-gray-50/50">
          {!selectedIncident ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Selecciona una incidencia para ver los comentarios</p>
              </div>
            </div>
          ) : (
            <>
              {/* Incident detail header */}
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_BADGE[selectedIncident.tipo_incidencia] || 'bg-gray-100 text-gray-700'}`}>
                      {selectedIncident.tipo_incidencia}
                    </span>
                    <p className="text-sm font-medium text-gray-800 mt-1">Exp: {selectedIncident.numero_expedicion}</p>
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(selectedIncident.fecha)}</p>
                </div>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-auto px-4 py-3">
                <h3 className="text-xs font-medium text-gray-500 uppercase mb-3">Comentarios</h3>
                {loadingComments ? (
                  <div className="flex items-center justify-center h-20 gap-2 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-xs">Cargando...</span>
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">Sin comentarios</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map(comment => (
                      <div key={comment.id} className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-sm text-gray-800">{comment.comentario}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDateTime(comment.created_at || comment.fecha)}</p>
                        {comment.usuario_nombre && <p className="text-xs text-gray-500 mt-0.5">{comment.usuario_nombre}</p>}
                        <PhotoThumbnails photos={comment.fotos} />
                      </div>
                    ))}
                  </div>
                )}
                <Pagination page={commentsPage} totalPages={commentsTotalPages} onPageChange={setCommentsPage} />
              </div>

              {/* Add comment form */}
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Añadir comentario..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input type="file" ref={commentFileRef} multiple accept="image/*" className="hidden" onChange={e => setCommentPhotos(Array.from(e.target.files))} />
                  <button onClick={() => commentFileRef.current?.click()} className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" title="Adjuntar fotos">
                    <Image className="w-4 h-4" />
                  </button>
                  <button onClick={addComment} disabled={savingComment || (!newComment.trim() && commentPhotos.length === 0)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {savingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                {commentPhotos.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Image className="w-3 h-3" />
                    <span>{commentPhotos.length} foto(s) adjunta(s)</span>
                    <button onClick={() => { setCommentPhotos([]); if (commentFileRef.current) commentFileRef.current.value = ''; }} className="text-red-500 hover:text-red-700">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════
  // ── MODALS ──
  // ════════════════════════════════════════════

  const renderAgencyModal = () => {
    if (!showAgencyModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAgencyModal(false)}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Nueva Agencia</h3>
            <button onClick={() => setShowAgencyModal(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input className={inputClass} value={agencyForm.nombre} onChange={e => setAgencyForm(p => ({ ...p, nombre: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsable Comercial</label>
              <input className={inputClass} value={agencyForm.responsable_comercial} onChange={e => setAgencyForm(p => ({ ...p, responsable_comercial: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo Comercial</label>
              <input type="email" className={inputClass} value={agencyForm.correo_comercial} onChange={e => setAgencyForm(p => ({ ...p, correo_comercial: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input className={inputClass} value={agencyForm.numero_telefono} onChange={e => setAgencyForm(p => ({ ...p, numero_telefono: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button onClick={() => setShowAgencyModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancelar</button>
            <button onClick={createAgency} disabled={savingAgency} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
              {savingAgency && <Loader2 className="w-4 h-4 animate-spin" />} Crear Agencia
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderIncidentModal = () => {
    if (!showIncidentModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowIncidentModal(false)}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Nueva Incidencia</h3>
            <button onClick={() => setShowIncidentModal(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Incidencia *</label>
              <select className={inputClass} value={incidentForm.tipo_incidencia} onChange={e => setIncidentForm(p => ({ ...p, tipo_incidencia: e.target.value }))}>
                {TIPO_INCIDENCIA_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N Expedición *</label>
              <input className={inputClass} value={incidentForm.numero_expedicion} onChange={e => setIncidentForm(p => ({ ...p, numero_expedicion: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <input type="date" className={inputClass} value={incidentForm.fecha} onChange={e => setIncidentForm(p => ({ ...p, fecha: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N Factura</label>
              <input className={inputClass} value={incidentForm.numero_factura} onChange={e => setIncidentForm(p => ({ ...p, numero_factura: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comentarios</label>
              <textarea className={inputClass + ' resize-none'} rows={3} value={incidentForm.comentarios} onChange={e => setIncidentForm(p => ({ ...p, comentarios: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fotos</label>
              <input type="file" ref={incidentFileRef} multiple accept="image/*" onChange={e => setIncidentPhotos(Array.from(e.target.files))} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
              {incidentPhotos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{incidentPhotos.length} archivo(s) seleccionado(s)</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button onClick={() => setShowIncidentModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancelar</button>
            <button onClick={createIncident} disabled={savingIncident} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
              {savingIncident && <Loader2 className="w-4 h-4 animate-spin" />} Crear Incidencia
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════
  // ── MAIN RENDER ──
  // ════════════════════════════════════════════

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {view === 'list' ? renderAgencyList() : renderIncidentsView()}
      {renderAgencyModal()}
      {renderIncidentModal()}
    </div>
  );
};

export default AgenciasTransporte;
